import React, { useEffect, useMemo, useState } from 'react';
import { getOrderProducts, createOrder, searchOrders, getOrder } from '../api';
import AppHeader from './AppHeader';
import { US_STATES } from '../utils/usStates';

const emptyBilling = {
  email: '',
  name: '',
  phone: '',
  address: '',
  address2: '',
  city: '',
  state: 'MO',
  zip: '',
  termsAccepted: false,
};

const emptyHolder = {
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirth: '',
  phone: '',
  address: '',
  address2: '',
  city: '',
  state: 'MO',
  zip: '',
};

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function Orders({ user, onLogout, onNavigate, initialOrderId }) {
  const [tab, setTab] = useState(initialOrderId ? 'lookup' : 'new');
  const [products, setProducts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [visitDates, setVisitDates] = useState({});
  const [passHolders, setPassHolders] = useState({});
  const [billing, setBilling] = useState(emptyBilling);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [completedOrder, setCompletedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialOrderId || '');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);


  useEffect(() => {
    getOrderProducts()
      .then((data) => {
        setProducts(data.products || []);
        setTaxRates(data.taxRates || []);
        const qty = {};
        (data.products || []).forEach((product) => {
          qty[product.code] = 0;
        });
        setQuantities(qty);
      })
      .catch((err) => setError(err.error || 'Unable to load products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!initialOrderId) return;
    getOrder(initialOrderId)
      .then((data) => {
        setSelectedOrder(data.order);
        setTab('lookup');
        setSearchQuery(data.order?.orderNumber || initialOrderId);
      })
      .catch((err) => setError(err.error || 'Unable to load order'));
  }, [initialOrderId]);

  const lineItems = useMemo(
    () =>
      products
        .map((product) => ({
          ...product,
          quantity: Number(quantities[product.code]) || 0,
        }))
        .filter((item) => item.quantity > 0),
    [products, quantities]
  );

  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.price, 0);
  const taxLines = taxRates.map((tax) => ({
    ...tax,
    amount: Math.round(subtotal * tax.rate * 100) / 100,
  }));
  const tax = Math.round(taxLines.reduce((sum, line) => sum + line.amount, 0) * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  const categories = [...new Set(products.map((product) => product.category))];

  const seasonPassSelections = useMemo(
    () =>
      products
        .filter((product) => product.createsSeasonPass)
        .map((product) => ({
          ...product,
          quantity: Number(quantities[product.code]) || 0,
          holders: passHolders[product.code] || [],
        }))
        .filter((item) => item.quantity > 0),
    [products, quantities, passHolders]
  );

  const handleQtyChange = (code, value) => {
    const qty = Math.max(0, Math.min(25, Number(value) || 0));
    const product = products.find((entry) => entry.code === code);
    setQuantities((prev) => ({ ...prev, [code]: qty }));
    if (product?.createsSeasonPass) {
      setPassHolders((prev) => {
        const current = prev[code] || [];
        const next = Array.from({ length: qty }, (_, index) => ({
          ...emptyHolder,
          ...(current[index] || {}),
        }));
        return { ...prev, [code]: next };
      });
    }
    if (!qty) {
      setVisitDates((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    }
  };

  const handleBillingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBilling((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleHolderChange = (productCode, index, e) => {
    const { name, value } = e.target;
    setPassHolders((prev) => {
      const holders = [...(prev[productCode] || [])];
      holders[index] = { ...holders[index], [name]: value };
      return { ...prev, [productCode]: holders };
    });
  };

  const fillHolderFromBilling = (productCode, index) => {
    const { firstName, lastName } = splitName(billing.name);
    setPassHolders((prev) => {
      const holders = [...(prev[productCode] || [])];
      holders[index] = {
        ...holders[index],
        firstName: firstName || holders[index]?.firstName || '',
        lastName: lastName || holders[index]?.lastName || '',
        email: billing.email || holders[index]?.email || '',
        phone: billing.phone || holders[index]?.phone || '',
        address: billing.address || holders[index]?.address || '',
        address2: billing.address2 || holders[index]?.address2 || '',
        city: billing.city || holders[index]?.city || '',
        state: billing.state || holders[index]?.state || 'MO',
        zip: billing.zip || holders[index]?.zip || '',
      };
      return { ...prev, [productCode]: holders };
    });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    for (const selection of seasonPassSelections) {
      const holders = selection.holders || [];
      if (holders.length !== selection.quantity) {
        setError(`Enter holder details for each ${selection.name}`);
        return;
      }
      for (let i = 0; i < holders.length; i += 1) {
        if (!holders[i].firstName?.trim() || !holders[i].lastName?.trim()) {
          setError(`First and last name are required for ${selection.name} #${i + 1}`);
          return;
        }
      }
    }

    for (const item of lineItems) {
      if (item.requiresVisitDate && !String(visitDates[item.code] || '').trim()) {
        setError(`Select a visit day for ${item.name}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const data = await createOrder({
        ...billing,
        items: lineItems.map((item) => ({
          productCode: item.code,
          quantity: item.quantity,
          holders: item.createsSeasonPass ? passHolders[item.code] || [] : undefined,
          visitDate: item.requiresVisitDate ? visitDates[item.code] : undefined,
        })),
      });
      setCompletedOrder(data.order);
      let emailNote = '';
      if (data.email?.sent) {
        emailNote = ` · confirmation emailed to ${data.order.email}`;
      } else if (data.email?.skipped) {
        emailNote = ' · confirmation email not configured';
      } else if (data.email && !data.email.sent) {
        emailNote = ' · confirmation email failed (order still saved)';
      }
      setMessage(`Order ${data.order.orderNumber} created${emailNote}`);
      setBilling(emptyBilling);
      setPassHolders({});
      setVisitDates({});
      setQuantities((prev) => {
        const reset = { ...prev };
        Object.keys(reset).forEach((key) => {
          reset[key] = 0;
        });
        return reset;
      });
    } catch (err) {
      setError(err.error || 'Unable to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchOrders = async (e) => {
    e.preventDefault();
    setError('');
    setSelectedOrder(null);
    try {
      const trimmed = searchQuery.trim();
      if (!trimmed) {
        setSearchResults([]);
        return;
      }
      const data = await searchOrders(trimmed);
      setSearchResults(data.results || []);
    } catch (err) {
      setError(err.error || 'Order search failed');
      setSearchResults([]);
    }
  };

  const collectBarcodeLabels = (order) => {
    if (!order) return [];
    const labels = [];
    (order.seasonPasses || []).forEach((pass) => {
      if (!pass.seasonPassId) return;
      labels.push({
        key: `pass-${pass.id || pass.seasonPassId}`,
        type: 'Season Pass',
        title: pass.productName || pass.productCode || 'Season Pass',
        detail: `${pass.firstName || ''} ${pass.lastName || ''}`.trim(),
        code: pass.seasonPassId,
      });
    });
    (order.items || []).forEach((item) => {
      (item.barcodeIds || []).forEach((code, index) => {
        const visitDate = item.visitDate || '';
        const usable =
          item.usableToday !== undefined
            ? item.usableToday
            : !visitDate || visitDate === new Date().toISOString().slice(0, 10);
        labels.push({
          key: `ticket-${item.id || item.productCode}-${index}-${code}`,
          type: 'Ticket',
          title: item.productName || item.productCode || 'Ticket',
          detail: [
            (item.barcodeIds || []).length > 1
              ? `Ticket ${index + 1} of ${item.barcodeIds.length}`
              : 'Admission / add-on',
            visitDate ? `Visit day ${visitDate}` : null,
            visitDate ? (usable ? 'Usable today' : 'Not usable today') : null,
          ]
            .filter(Boolean)
            .join(' · '),
          code,
          usable: visitDate ? usable : true,
        });
      });
    });
    return labels;
  };

  const renderBarcodeLabels = (order) => {
    const labels = collectBarcodeLabels(order);
    if (!labels.length) {
      return <p className="subtitle">No barcode labels on this order.</p>;
    }
    return (
      <div className="table-scroll">
        <table className="users-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Product</th>
              <th>Detail</th>
              <th>Status</th>
              <th>Barcode</th>
            </tr>
          </thead>
          <tbody>
            {labels.map((label) => (
              <tr key={label.key} className={label.usable === false ? 'compensation-row-inactive' : ''}>
                <td>{label.type}</td>
                <td>{label.title}</td>
                <td>{label.detail || '—'}</td>
                <td>
                  {label.usable === false
                    ? 'Not usable today'
                    : label.usable === true && String(label.detail || '').includes('Visit day')
                      ? 'Usable today'
                      : '—'}
                </td>
                <td>
                  <code>{label.code}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSeasonPasses = (passes) => {
    if (!passes?.length) {
      return <p className="subtitle">No season passes on this order.</p>;
    }
    return (
      <div className="table-scroll">
        <table className="users-table">
          <thead>
            <tr>
              <th>Season Pass ID</th>
              <th>Pass Type</th>
              <th>Name</th>
              <th>Email</th>
              <th>Entitlements</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {passes.map((pass) => (
              <tr key={pass.id}>
                <td>{pass.seasonPassId}</td>
                <td>{pass.productName || pass.productCode || '—'}</td>
                <td>
                  {pass.firstName} {pass.lastName}
                </td>
                <td>{pass.email}</td>
                <td>
                  {(pass.entitlements || []).length ? (
                    <ul className="entitlements-list compact">
                      {(pass.entitlements || []).map((item, index) => (
                        <li key={`${typeof item === 'string' ? item : item?.title}-${index}`}>
                          {typeof item === 'string'
                            ? item
                            : [item?.category, item?.title].filter(Boolean).join(': ') ||
                              item?.description ||
                              'Perk'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-table"
                    onClick={() =>
                      onNavigate('season-pass-detail', {
                        seasonPassId: pass.id || pass.seasonPassId,
                      })
                    }
                  >
                    Open Pass
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container wide-container order-page">
      <AppHeader
        user={user}
        pageTitle="Orders"
        subtitle="Daily tickets & season passes — Enchanted Parks 2026"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="orders"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="admin-tabs">
        <button
          type="button"
          className={tab === 'new' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setTab('new')}
        >
          New Order
        </button>
        <button
          type="button"
          className={tab === 'lookup' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setTab('lookup')}
        >
          Find Order
        </button>
      </div>

      {tab === 'new' && (
        <>
          <h2 className="order-hero-title">Daily Tickets & Season Passes</h2>
          {loading ? (
            <div className="no-results">Loading products…</div>
          ) : (
            <form onSubmit={handleSubmitOrder}>
              {categories.map((category) => (
                <div key={category} className="order-category">
                  <h3>{category}</h3>
                  <table className="order-products-table">
                    <thead>
                      <tr>
                        <th>Tickets</th>
                        <th>Price</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products
                        .filter((product) => product.category === category)
                        .map((product) => (
                          <tr key={product.code}>
                            <td>
                              <strong>{product.name}</strong>
                              <p className="order-product-desc">{product.description}</p>
                              {product.createsSeasonPass && (
                                <span className="order-pass-badge">Creates season pass</span>
                              )}
                              {product.requiresVisitDate && (
                                <span className="order-pass-badge">Valid only on selected day</span>
                              )}
                              {product.requiresVisitDate &&
                                Number(quantities[product.code] || 0) > 0 && (
                                  <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                                    <label htmlFor={`visit-${product.code}`}>Visit day *</label>
                                    <input
                                      id={`visit-${product.code}`}
                                      type="date"
                                      required
                                      value={visitDates[product.code] || ''}
                                      onChange={(e) =>
                                        setVisitDates((prev) => ({
                                          ...prev,
                                          [product.code]: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                )}
                            </td>
                            <td className="order-price">{money(product.price)}</td>
                            <td>
                              <select
                                aria-label={`Quantity for ${product.name}`}
                                value={quantities[product.code] || 0}
                                onChange={(e) => handleQtyChange(product.code, e.target.value)}
                              >
                                {Array.from({ length: 26 }, (_, i) => (
                                  <option key={i} value={i}>
                                    {i}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="order-totals">
                <div className="order-totals-row">
                  <span>Subtotal</span>
                  <span>{money(subtotal)}</span>
                </div>
                {taxLines.map((line) => (
                  <div key={line.code} className="order-totals-row muted">
                    <span>
                      {line.label} ({(line.rate * 100).toFixed(3)}%)
                    </span>
                    <span>{money(line.amount)}</span>
                  </div>
                ))}
                <div className="order-totals-row total">
                  <span>Total</span>
                  <span>{money(total)}</span>
                </div>
              </div>

              <div className="order-billing">
                <h3>Billing Information</h3>
                <p className="subtitle">
                  Terms &amp; Conditions:{' '}
                  <a
                    href="https://mid-americaparks.enchantedparks.com/terms/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    mid-americaparks.enchantedparks.com/terms
                  </a>
                </p>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="termsAccepted"
                      checked={billing.termsAccepted}
                      onChange={handleBillingChange}
                    />{' '}
                    I have read and agree to the terms above. *
                  </label>
                </div>
                <div className="form-group">
                  <label htmlFor="order-email">Email *</label>
                  <input
                    id="order-email"
                    name="email"
                    type="email"
                    value={billing.email}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="order-name">Name *</label>
                  <input
                    id="order-name"
                    name="name"
                    value={billing.name}
                    onChange={handleBillingChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="order-phone">Phone Number</label>
                  <input
                    id="order-phone"
                    name="phone"
                    value={billing.phone}
                    onChange={handleBillingChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="order-address">Address</label>
                  <input
                    id="order-address"
                    name="address"
                    value={billing.address}
                    onChange={handleBillingChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="order-address2">Address 2</label>
                  <input
                    id="order-address2"
                    name="address2"
                    value={billing.address2}
                    onChange={handleBillingChange}
                  />
                </div>
                <div className="order-address-grid">
                  <div className="form-group">
                    <label htmlFor="order-city">City</label>
                    <input
                      id="order-city"
                      name="city"
                      value={billing.city}
                      onChange={handleBillingChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="order-state">State</label>
                    <select
                      id="order-state"
                      name="state"
                      value={billing.state}
                      onChange={handleBillingChange}
                    >
                      {US_STATES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="order-zip">Zip</label>
                    <input
                      id="order-zip"
                      name="zip"
                      value={billing.zip}
                      onChange={handleBillingChange}
                    />
                  </div>
                </div>
              </div>

              {seasonPassSelections.length > 0 && (
                <div className="pass-holders-section">
                  <h3>Season Pass Holder Information</h3>
                  <p className="subtitle">
                    Enter details for each selected season pass. Use billing info to
                    copy the purchaser details onto a holder.
                  </p>
                  {seasonPassSelections.map((selection) => (
                    <div key={selection.code} className="pass-holder-group">
                      <h4>
                        {selection.name} ({selection.quantity})
                      </h4>
                      {(selection.holders || []).map((holder, index) => (
                        <div
                          key={`${selection.code}-${index}`}
                          className="pass-holder-card"
                        >
                          <div className="pass-holder-card-header">
                            <strong>Pass #{index + 1}</strong>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => fillHolderFromBilling(selection.code, index)}
                            >
                              Use billing info
                            </button>
                          </div>
                          <div className="order-address-grid">
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-firstName`}>
                                First Name *
                              </label>
                              <input
                                id={`${selection.code}-${index}-firstName`}
                                name="firstName"
                                value={holder.firstName}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-lastName`}>
                                Last Name *
                              </label>
                              <input
                                id={`${selection.code}-${index}-lastName`}
                                name="lastName"
                                value={holder.lastName}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-dateOfBirth`}>
                                Date of Birth
                              </label>
                              <input
                                id={`${selection.code}-${index}-dateOfBirth`}
                                name="dateOfBirth"
                                type="date"
                                value={holder.dateOfBirth}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label htmlFor={`${selection.code}-${index}-email`}>Email</label>
                            <input
                              id={`${selection.code}-${index}-email`}
                              name="email"
                              type="email"
                              value={holder.email}
                              onChange={(e) =>
                                handleHolderChange(selection.code, index, e)
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor={`${selection.code}-${index}-phone`}>
                              Phone Number
                            </label>
                            <input
                              id={`${selection.code}-${index}-phone`}
                              name="phone"
                              value={holder.phone}
                              onChange={(e) =>
                                handleHolderChange(selection.code, index, e)
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor={`${selection.code}-${index}-address`}>
                              Address
                            </label>
                            <input
                              id={`${selection.code}-${index}-address`}
                              name="address"
                              value={holder.address}
                              onChange={(e) =>
                                handleHolderChange(selection.code, index, e)
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor={`${selection.code}-${index}-address2`}>
                              Address 2
                            </label>
                            <input
                              id={`${selection.code}-${index}-address2`}
                              name="address2"
                              value={holder.address2}
                              onChange={(e) =>
                                handleHolderChange(selection.code, index, e)
                              }
                            />
                          </div>
                          <div className="order-address-grid">
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-city`}>City</label>
                              <input
                                id={`${selection.code}-${index}-city`}
                                name="city"
                                value={holder.city}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-state`}>
                                State
                              </label>
                              <select
                                id={`${selection.code}-${index}-state`}
                                name="state"
                                value={holder.state}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                              >
                                {US_STATES.map((st) => (
                                  <option key={st} value={st}>
                                    {st}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label htmlFor={`${selection.code}-${index}-zip`}>
                                Zip Code
                              </label>
                              <input
                                id={`${selection.code}-${index}-zip`}
                                name="zip"
                                value={holder.zip}
                                onChange={(e) =>
                                  handleHolderChange(selection.code, index, e)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <div className="order-submit-row">
                <button type="submit" disabled={submitting || total <= 0}>
                  {submitting ? 'Submitting…' : 'Submit Order'}
                </button>
              </div>
            </form>
          )}

          {completedOrder && (
            <div className="order-confirmation search-results">
              <h2>Order {completedOrder.orderNumber} complete</h2>
              <p>
                Total {money(completedOrder.total)} · {completedOrder.email}
              </p>
              <h3>Barcode labels</h3>
              {renderBarcodeLabels(completedOrder)}
              <h3>Season passes on this order</h3>
              {renderSeasonPasses(completedOrder.seasonPasses)}
            </div>
          )}
        </>
      )}

      {tab === 'lookup' && (
        <div>
          <form onSubmit={handleSearchOrders}>
            <div className="search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Order number, email, name, or phone..."
              />
              <button type="submit">Search Orders</button>
            </div>
          </form>

          {searchResults.length > 0 && (
            <div className="search-results">
              <h2>Orders ({searchResults.length})</h2>
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Total</th>
                    <th>Passes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((order) => (
                    <tr key={order.id}>
                      <td>{order.orderNumber}</td>
                      <td>{order.name}</td>
                      <td>{order.email}</td>
                      <td>{money(order.total)}</td>
                      <td>{order.seasonPasses?.length || 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-table"
                          onClick={() => setSelectedOrder(order)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedOrder && (
            <div className="search-results order-detail">
              <h2>Order {selectedOrder.orderNumber}</h2>
              <p className="subtitle">
                {selectedOrder.name} · {selectedOrder.email} · {selectedOrder.phone}
              </p>
              <p className="subtitle">
                {[selectedOrder.address, selectedOrder.address2, selectedOrder.city, selectedOrder.state, selectedOrder.zip]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              <h3>Items</h3>
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Visit day</th>
                    <th>Unit</th>
                    <th>Line</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOrder.items || []).map((item) => (
                    <tr key={item.id}>
                      <td>{item.productName}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {item.visitDate || '—'}
                        {item.visitDate
                          ? item.usableToday
                            ? ' · usable today'
                            : ' · not usable today'
                          : ''}
                      </td>
                      <td>{money(item.unitPrice)}</td>
                      <td>{money(item.quantity * item.unitPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="order-totals">
                <div className="order-totals-row">
                  <span>Subtotal</span>
                  <span>{money(selectedOrder.subtotal)}</span>
                </div>
                <div className="order-totals-row muted">
                  <span>Tax</span>
                  <span>{money(selectedOrder.tax)}</span>
                </div>
                <div className="order-totals-row total">
                  <span>Total</span>
                  <span>{money(selectedOrder.total)}</span>
                </div>
              </div>
              <h3>Barcode labels</h3>
              {renderBarcodeLabels(selectedOrder)}
              <h3>Season passes on this order</h3>
              {renderSeasonPasses(selectedOrder.seasonPasses)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Orders;
